/**
 * Hook for resolving skin-specific styles based on the current skin context.
 *
 * Retrieves the appropriate style object for the active skin from the provided skin styles map.
 * Falls back to the default style if the current skin is not found in the map.
 * Memoizes the result to prevent unnecessary recalculations.
 *
 * @template T - The style object type, must extend Record<string, unknown>
 * @param {SkinStylesMap<T>} skinStyles - Map of skin names to their corresponding styles or style factories
 * @returns {T} The resolved style object for the current skin
 *
 * @example
 * const styles = useSkinStyles({
 *   [SKIN.BET365]: { color: 'blue' },
 *   [SKIN.GOLA]: { color: 'red' }
 * });
 */
import { useMemo } from 'react';
import { useSkin } from '../contexts/SkinContext';
import { Skin } from '../types/enums';
import { DEFAULT_SKIN } from '../constants/marketplace';

type DefaultSkin = typeof DEFAULT_SKIN;

export type SkinStylesMap<T> = Partial<Record<Skin, T | (() => T)>> & Record<DefaultSkin, T | (() => T)>;

export const resolveStyle = <T>(style: T | (() => T)): T => {
  return typeof style === 'function' ? (style as () => T)() : style;
};

export const useSkinStyles = <T extends Record<string, unknown>>(skinStyles: SkinStylesMap<T>) => {
  const { skin } = useSkin();

  return useMemo(() => {
    const selectedStyle = skinStyles[skin] ?? skinStyles[DEFAULT_SKIN];
    return resolveStyle(selectedStyle);
  }, [skin, skinStyles]);
};
